import React, { useEffect, useState } from "react";
import { Modal, Dropdown } from "react-bootstrap";
import { CreditCardIcon, CardIcon, PlusIcon, HolderIcon, LockIcon } from "./icons/icons";
import { instance } from "../../../utils/utils";
import { getBackendError } from "../../../utils/error-helpers";
import InlineModal from "../../info-modal/infoModal";
import "../styles.css"

const DOCUMENT_TYPES = [
    { label: "Pasaporte", value: 1 },
    { label: "Licencia de conducción", value: 2 },
    { label: "Seguridad social", value: 3 },
    { label: "Cédula de Ciudadanía", value: 4 },
    { label: "Cédula de Extranjería", value: 5 },
    { label: "NIT", value: 6 },
    { label: "Documento Nacional de Identificación", value: 68 },
    { label: "Tarjeta de Identidad", value: 100 },
];

export default function AddPaymentMethodModal({
    show,
    onHide,
    sourceOrigin,
    onSuccess,
}) {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(false);

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [loading, setLoading] = useState(false);

    const [newCardData, setNewCardData] = useState({
        holder_name: "",
        holder_last_name: "",
        document_type: 4,
        document_number: "",
        card_number: "",
        expiration: "",
        cvv: "",
        save_card: true,
    });
    const [infoModal, setInfoModal] = useState(null);

    const errors = {};

    const handlePayment = async () => {
        if (loading) return;

        setLoading(true);
        try {
            if (selectedPaymentMethod === "new") {
                const payload = {
                    cardholder_name: newCardData.holder_name,
                    cardholder_last_name: newCardData.holder_last_name,
                    identification_type: newCardData.document_type,
                    identification: newCardData.document_number,
                    card_number: newCardData.card_number,
                    cvv: newCardData.cvv,
                    expiration: newCardData.expiration,
                };

                await instance.post(
                    "/customers/company/payment-method/create",
                    payload
                );

                await getPaymentMethods();
                setSelectedPaymentMethod(null);

                if (onSuccess) onSuccess();

                setInfoModal({
                    iconType: "check",
                    title: "Método de pago actualizado",
                    description: "Hemos guardado los datos de tu tarjeta con éxito.",
                    secondaryAction: {
                        label: "Cerrar",
                        onClick: () => {
                            setInfoModal(null);
                            if (sourceOrigin) {
                                onHide();
                            }
                        },
                    },
                });

                return;
            }
            if (
                selectedPaymentMethod &&
                !selectedCard?.is_default
            ) {
                await instance.patch(
                    `/customers/company/payment-method/${selectedPaymentMethod}/default`
                );

                await getPaymentMethods();

                if (onSuccess) onSuccess();

                setInfoModal({
                    iconType: "check",
                    title: "Cambio realizado",
                    description: "Este método de pago ahora es el predeterminado.",
                    secondaryAction: {
                        label: "Cerrar",
                        onClick: () => setInfoModal(null),
                    },
                });
            }

        } catch (error) {
            console.error("Error actualizando método de pago", error);

            const errorMessage = getBackendError(error);
            setInfoModal({
                iconType: "error",
                title: "Error",
                description: errorMessage,
                secondaryAction: {
                    label: "Cerrar",
                    onClick: () => setInfoModal(null),
                },
            });
        } finally {
            setLoading(false);
        }
    };


    const getPaymentMethods = async () => {
        setLoadingMethods(true);
        try {
            const { data } = await instance.get(
                "/customers/company/payment-method/list"
            );
            const methods = data.payment_methods || [];
            setPaymentMethods(methods);

            if (!sourceOrigin) {
                const defaultMethod = methods.find(m => m.is_default);
                if (defaultMethod) {
                    setSelectedPaymentMethod(defaultMethod.id);
                }
            }
        } catch (err) {
            console.error("Error cargando métodos de pago", err);
        } finally {
            setLoadingMethods(false);
        }
    };

    const handleDeleteCard = async () => {
        if (!selectedPaymentMethod || selectedPaymentMethod === "new") return;

        const selectedCard = paymentMethods.find(
            m => m.id === selectedPaymentMethod
        );

        if (selectedCard?.is_default && paymentMethods.length <= 1) {
            return;
        }

        try {
            setLoading(true);

            const { data } = await instance.delete(
                `/customers/company/payment-method/${selectedPaymentMethod}/delete`
            );
            console.log(data);
            setSelectedPaymentMethod(null);
            await getPaymentMethods();

            setInfoModal({
                iconType: "check",
                title: "Cambio realizado",
                description: "La tarjeta ha sido eliminada correctamente.",
                secondaryAction: {
                    label: "Cerrar",
                    onClick: () => {
                        setInfoModal(null);
                    },
                },
            });

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!show) return;
        getPaymentMethods();
        if (sourceOrigin) {
            setSelectedPaymentMethod("new");
        }
    }, [show]);


    const isNewCardInvalid =
        selectedPaymentMethod === "new" &&
        (
            !newCardData.holder_name ||
            !newCardData.holder_last_name ||
            !newCardData.document_type ||
            !newCardData.document_number ||
            !newCardData.card_number ||
            !newCardData.expiration ||
            !newCardData.cvv
        );

    const selectedCard = paymentMethods?.find(
        m => m.id === selectedPaymentMethod
    );

    const canDeleteCard =
        selectedPaymentMethod &&
        selectedPaymentMethod !== "new" &&
        !loading &&
        (!selectedCard?.is_default || paymentMethods.length > 1);

    const isSaveDisabled =
        loading ||
        (selectedPaymentMethod === "new" && isNewCardInvalid) ||
        (selectedPaymentMethod !== "new" && selectedCard?.is_default);

    const getButtonText = () => {
        if (loading) return "Procesando...";
        if (selectedPaymentMethod !== "new" && !selectedCard?.is_default) {
            return "Seleccionar como Predeterminado";
        }
        return "Guardar";
    };

    return (
        <>
            <Modal show={show} centered backdrop="static" size="sm" scrollable={false} >
                <Modal.Body className="p-0">
                    <div className="subscription-edit-payment-methods subscription-edit-container p-5 pt-3 d-flex flex-column gap-3">

                        <div className="d-flex justify-content-between align-items-center pt-3">
                            <h5 className="subscription-edit-section-title d-flex align-items-center gap-2 m-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="6" width="18" height="13" rx="2" stroke="#9DACBD" stroke-width="2" />
                                    <path d="M7 15H7.01" stroke="#9DACBD" stroke-width="2" stroke-linecap="round" />
                                    <path d="M4 11H21" stroke="#9DACBD" stroke-width="2" stroke-linecap="round" />
                                </svg>
                                Tu método de pago
                            </h5>

                            {!sourceOrigin && (
                                <svg className="cursor-pointer" onClick={onHide} xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
                                    <path d="M14.0605 1.5C20.9574 1.50007 26.6211 7.26551 26.6211 14.4678C26.6209 21.6698 20.9572 27.4345 14.0605 27.4346C7.1638 27.4346 1.50022 21.6699 1.5 14.4678C1.5 7.26547 7.16367 1.5 14.0605 1.5Z" stroke="#9DACBD" stroke-width="3" />
                                    <path d="M8.65234 9.10938L18.9273 19.2901" stroke="#9DACBD" stroke-width="2" stroke-linecap="round" />
                                    <path d="M18.9272 9.10938L8.65232 19.2901" stroke="#9DACBD" stroke-width="2" stroke-linecap="round" />
                                </svg>
                            )}
                        </div>

                        {loadingMethods && (
                            <div className="subscription-edit-payment-methods d-flex flex-column gap-3">
                                <div className="d-flex justify-content-between align-items-center p-3 skeleton">
                                </div>
                                <div className="d-flex justify-content-between align-items-center p-3 skeleton">
                                </div>
                            </div>
                        )}


                        {!loadingMethods && paymentMethods?.length === 0 && selectedPaymentMethod !== "new" && (
                            <div className="payment-method-empty d-flex flex-column align-items-center text-center p-4 gap-2">
                                <span className="payment-card-title">
                                    No tienes una tarjeta de crédito registrada. Agrega un método de pago para habilitar las renovaciones y compras en la plataforma.
                                </span>
                            </div>
                        )}

                        <div className="subscription-edit-payment-methods d-flex flex-column gap-3">
                            {!loadingMethods &&
                                paymentMethods?.map(method => (
                                    <div
                                        key={method.id}
                                        className={`payment-method-item d-flex justify-content-between align-items-center p-3 ${selectedPaymentMethod === method.id
                                            ? "selected"
                                            : ""
                                            }`}
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <input
                                                type="radio"
                                                name="payment-method"
                                                checked={selectedPaymentMethod === method.id}
                                                onChange={() =>
                                                    setSelectedPaymentMethod(method.id)
                                                }
                                            />
                                            <div className="d-flex flex-column">
                                                <span className="payment-card-title">
                                                    {method.franchise} •••• {method.last_four_digits}
                                                </span>
                                                <span className="payment-card-subtitle">
                                                    Vence {method.expiration}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="payment-card-actions d-flex align-items-center gap-3">
                                            {method.is_default && (
                                                <span className="payment-default">
                                                    Predeterminado
                                                </span>
                                            )}
                                            <CardIcon />
                                        </div>
                                    </div>
                                ))}

                            <div
                                className={`payment-method-add d-flex justify-content-between align-items-center p-3 ${selectedPaymentMethod === "new" ? "selected" : ""}`}
                            >
                                <div className="d-flex align-items-center gap-3">
                                    <input
                                        type="radio"
                                        name="payment-method"
                                        checked={selectedPaymentMethod === "new"}
                                        onChange={() => setSelectedPaymentMethod("new")}
                                    />
                                    <span className="payment-add-text">
                                        {!loadingMethods && paymentMethods?.length === 0 ? "Agregar" : "Usar otro"} método de pago
                                    </span>
                                </div>
                                <PlusIcon />
                            </div>

                            {selectedPaymentMethod === "new" && (
                                <>
                                    <div className="subscription-holder-info card p-3 d-flex flex-column gap-3">
                                        <h5 className="subscription-edit-section-title d-flex align-items-center gap-2 m-0">
                                            <HolderIcon />
                                            Información del titular
                                        </h5>

                                        <div className="d-flex flex-row gap-3">
                                            <div className="w-50 d-flex flex-column gap-2">
                                                <label className="form-label">
                                                    Nombre
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control subscription-edit-input"
                                                    value={newCardData.holder_name}
                                                    onChange={(e) =>
                                                        setNewCardData({ ...newCardData, holder_name: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <div className="w-50 d-flex flex-column gap-2">
                                                <label className="form-label">
                                                    Apellido(s)
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control subscription-edit-input"
                                                    value={newCardData.holder_last_name}
                                                    onChange={(e) =>
                                                        setNewCardData({
                                                            ...newCardData,
                                                            holder_last_name: e.target.value,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="d-flex flex-column flex-md-row gap-3">
                                            <div className="w-50 d-flex flex-column gap-2">
                                                <label className="form-label">Tipo de documento</label>

                                                <Dropdown className="w-100 subscription-edit-select">
                                                    <Dropdown.Toggle
                                                        className="w-100 d-flex justify-content-between align-items-center subscription-edit-dropdown-btn"
                                                    >
                                                        {
                                                            DOCUMENT_TYPES.find(
                                                                d => d.value === newCardData.document_type
                                                            )?.label || "Seleccionar..."
                                                        }
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10" fill="none">
                                                            <path d="M7.54236 9.08938C7.14647 9.08938 6.75052 8.93837 6.4484 8.63626L0.45311 2.64096C-0.151037 2.03681 -0.151037 1.05726 0.45311 0.453101C1.05723 -0.151034 2.03682 -0.151034 2.64095 0.453101L7.54236 5.35457L12.4438 0.453101C13.0479 -0.151034 14.0275 -0.151034 14.6316 0.453101C15.2358 1.05726 15.2358 2.03681 14.6316 2.64096L8.63633 8.63626C8.33423 8.93837 7.93825 9.08938 7.54236 9.08938Z" fill="#9DACBD" />
                                                        </svg>
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu className="subscription-edit-select-menu w-100">
                                                        {DOCUMENT_TYPES.map(opt => (
                                                            <Dropdown.Item
                                                                key={opt.value}
                                                                onClick={() =>
                                                                    setNewCardData({
                                                                        ...newCardData,
                                                                        document_type: opt.value,
                                                                    })
                                                                }
                                                            >
                                                                {opt.label}
                                                            </Dropdown.Item>
                                                        ))}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>

                                            <div className="w-50 d-flex flex-column gap-2">
                                                <label className="form-label">Número de documento</label>
                                                <input
                                                    type="text"
                                                    className="form-control subscription-edit-input"
                                                    value={newCardData.document_number}
                                                    onChange={(e) =>
                                                        setNewCardData({ ...newCardData, document_number: e.target.value })
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="subscription-payment-info card p-3 d-flex flex-column gap-3">
                                        <h5 className="subscription-edit-section-title d-flex align-items-center gap-2 m-0">
                                            <CreditCardIcon className="subscription-edit-section-title-icon" />
                                            Método de pago
                                        </h5>

                                        <div className="d-flex flex-column gap-2">
                                            <label className="form-label">Número de tarjeta</label>
                                            <input
                                                type="text"
                                                className="form-control subscription-edit-input"
                                                value={newCardData.card_number}
                                                onChange={(e) =>
                                                    setNewCardData({ ...newCardData, card_number: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="d-flex flex-column flex-md-row gap-3">
                                            <div className="w-50 d-flex flex-column gap-2">
                                                <label className="form-label">Vencimiento (MM/AA)</label>
                                                <input
                                                    type="text"
                                                    className="form-control subscription-edit-input"
                                                    value={newCardData.expiration}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, "");
                                                        if (value.length > 4) value = value.slice(0, 4);
                                                        if (value.length >= 2 && e.nativeEvent.inputType !== 'deleteContentBackward') {
                                                            value = `${value.slice(0, 2)}/${value.slice(2)}`;
                                                        }
                                                        setNewCardData({ ...newCardData, expiration: value });
                                                    }}
                                                />
                                            </div>

                                            <div className="w-50 d-flex flex-column gap-2">
                                                <label className="form-label">CVV</label>
                                                <input
                                                    type="text"
                                                    className="form-control subscription-edit-input"
                                                    value={newCardData.cvv}
                                                    onChange={(e) =>
                                                        setNewCardData({ ...newCardData, cvv: e.target.value })
                                                    }
                                                />
                                            </div>
                                        </div>

                                    </div>
                                </>
                            )}

                            <div className="d-flex flex-column justify-content-center align-items-center gap-3 mt-2">
                                <button
                                    className={
                                        "subscription-edit-pay-btn delete-btn w-100 d-flex justify-content-center align-items-center " +
                                        (!canDeleteCard ? "disabled" : "")
                                    }
                                    onClick={canDeleteCard ? handleDeleteCard : undefined}
                                    disabled={!canDeleteCard}
                                >
                                    Eliminar tarjeta
                                </button>
                                <button
                                    className={
                                        "subscription-edit-pay-btn w-100 d-flex justify-content-center align-items-center " +
                                        (isSaveDisabled ? "disabled" : "")
                                    }
                                    onClick={isSaveDisabled ? undefined : handlePayment}
                                    disabled={isSaveDisabled}
                                >
                                    <LockIcon />
                                    {getButtonText()}
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            {infoModal && (
                <InlineModal
                    show={true}
                    onClose={() => setInfoModal(null)}
                    title={infoModal?.title}
                    description={infoModal?.description}
                    iconType={infoModal?.iconType}
                    primaryAction={infoModal?.primaryAction}
                    secondaryAction={infoModal?.secondaryAction}
                />
            )}
        </>
    );
}
